import { useState, useRef, useEffect, useCallback, useMemo } from 'react'
import { useParams, useNavigate, useSearchParams } from 'react-router-dom'
import TopNav from '../../components/TopNav'
import ContextBar from '../../components/ContextBar'
import ChatInput from './components/ChatInput'
import Message from './components/Message'
import StepsCard from './components/StepsCard'
import ClarificationCard from './components/ClarificationCard'
import CompareCard from './components/CompareCard'
import ErrorCard from './components/ErrorCard'
import CompletionSummary from './components/CompletionSummary'
import DataViewer from './components/DataViewer'
import DataTab from './components/DataTab'
import FindingsTab from './components/FindingsTab'
import ActionsTab from './components/ActionsTab'
import ExportTab from './components/ExportTab'
import {
  chat, getCustomer, getSchemas, getUploads, getConnections, getTablePreview,
  createWorkEffort, getWorkEffort, appendWorkEffortMessage, updateWorkEffortState,
  approveOrchApproach, getOrchSession, submitOrchClarification, runWorkEffort,
  pinMetric, getObservations, dismissObservation,
} from '../../api'
import { createCoalescedSaver } from '../../utils/coalescedSave'
import { dataSourcesFrom, hasDataSources } from '../../utils/workEffortSources'
import LoadError from '../../components/LoadError'
import './WorkEffort.css'
import { saveErrorText } from './saveError'
import { API_BASE as API_BASE_CONFIG } from '../../config'
import { loadErrorMessage } from '../../utils/loadState'
import { findingTier, couldNotCheck, summaryTotal, summaryWithout } from '../../utils/findingTier'

// A chat clarification (the company question among them) is not an investigation step,
// and a company scope is remembered per person, not per customer.
function clarificationCardLabels(clarification) {
  if (clarification?.orchConcept) return {}
  const key = clarification?.clarificationKey || clarification?.key || ''
  return {
    submitLabel: 'Continue →',
    ...(clarification?.rememberLabel || key.startsWith('company_scope:')
      ? { rememberLabel: clarification.rememberLabel || 'Remember my choice' }
      : {}),
  }
}

export default function WorkEffort() {
  const { customerId: CUSTOMER_ID, effortId: EFFORT_ID_PARAM } = useParams()
  const navigate = useNavigate()
  const [searchParams] = useSearchParams()
  const prefillQuestion = searchParams.get('q')

  // ─── Persistence ──────────────────────────────────────────
  const [effortId, setEffortId] = useState(EFFORT_ID_PARAM === 'new' ? null : EFFORT_ID_PARAM)
  const [loaded, setLoaded] = useState(false)

  // ─── Core State ─────────────────────────────────────────
  const [workState, setWorkState] = useState('empty')
  const [activeTab, setActiveTab] = useState('plan')
  const chatScrollRef = useRef(null)

  // ─── Customer Context ───────────────────────────────────
  const [customer, setCustomer] = useState(null)
  const [customerLoading, setCustomerLoading] = useState(true)

  // ─── Chat Messages ──────────────────────────────────────
  const [messages, setMessages] = useState([])
  const [chatLoading, setChatLoading] = useState(false)

  // ─── Investigation Plan ─────────────────────────────────
  const [planSteps, setPlanSteps] = useState([])
  const [planTitle, setPlanTitle] = useState('')

  // ─── Running State ──────────────────────────────────────
  const [runningSteps, setRunningSteps] = useState([])
  const [runProgress, setRunProgress] = useState(0)
  const [currentStepIndex, setCurrentStepIndex] = useState(0)

  // ─── Clarification ──────────────────────────────────────
  const [clarification, setClarification] = useState(null)

  // ─── Error ──────────────────────────────────────────────
  const [stepError, setStepError] = useState(null)
  // A save the server refused: shown until dismissed or until a later save succeeds.
  const [saveError, setSaveError] = useState(null)

  // ─── Results / Findings ─────────────────────────────────
  const [findings, setFindings] = useState([])
  const [actions, setActions] = useState([])
  const [compareMeta, setCompareMeta] = useState(null)
  const [completionStats, setCompletionStats] = useState([])
  const [orchApproachSteps, setOrchApproachSteps] = useState([])
  const [finalNarrative, setFinalNarrative] = useState('')
  const [selectedFinding, setSelectedFinding] = useState(null) // Finding clicked for data view
  const [observationDrillCache, setObservationDrillCache] = useState({}) // obsId -> {rows, columns, loading, error}

  // Fetch drill-down rows for an observation finding
  const handleFindingSelect = async (index) => {
    setSelectedFinding(index)
    if (index === null) return
    // Find the finding at this index in the merged list — we need to recompute
    // We'll handle this via observationDrillCache keyed by observationId
  }
  const [selectedDataSource, setSelectedDataSource] = useState(null) // Data source clicked for preview
  const [selectedProvenance, setSelectedProvenance] = useState(null) // Provenance source clicked for detail view
  const [knowledgeSources, setKnowledgeSources] = useState([]) // ChromaDB chunks used by orchestrator

  // ─── Data Sources (right panel) ─────────────────────────
  // errors: the read that failed, per list -- a failed read is said, never shown as empty
  const [dataSources, setDataSources] = useState({ connections: [], uploads: [], tables: [], errors: {} })

  // ─── Observations (auto-findings) ─────────────────────
  const [observations, setObservations] = useState([])
  const [observationsLoading, setObservationsLoading] = useState(false)
  // the route's active-finding counts by tier (finding_status.count_by_tier; FYI not counted)
  const [observationsSummary, setObservationsSummary] = useState(null)
  const [observationsError, setObservationsError] = useState(null)

  // ─── Export ─────────────────────────────────────────────
  const [exportResult, setExportResult] = useState(null)

  // ─── Conversation history (sent with each /api/chat call)
  const [conversationHistory, setConversationHistory] = useState([])

  // ─── Orchestrator session (when complex question detected)
  const [orchSessionId, setOrchSessionId] = useState(null)

  // =================================================================
  // PERSISTENCE: Load existing work effort or create new one
  // =================================================================

  useEffect(() => {
    async function initWorkEffort() {
      if (EFFORT_ID_PARAM && EFFORT_ID_PARAM !== 'new') {
        try {
          const effort = await getWorkEffort(EFFORT_ID_PARAM)
          restoreFromEffort(effort)
          setEffortId(EFFORT_ID_PARAM)
        } catch (err) {
          console.error('Failed to load work effort:', err)
        }
      }
      setLoaded(true)
    }
    initWorkEffort()
  }, [EFFORT_ID_PARAM])

  // Auto-send prefill question from ?q= param (e.g. from dashboard "Investigate" button)
  const prefillSentRef = useRef(false)
  useEffect(() => {
    if (loaded && prefillQuestion && !prefillSentRef.current && workState === 'empty') {
      prefillSentRef.current = true
      handleSend(prefillQuestion)
    }
  }, [loaded, prefillQuestion, workState])

  function restoreFromEffort(effort) {
    setWorkState(effort.state || 'empty')
    setMessages(effort.messages || [])
    setPlanSteps(effort.plan_steps || [])
    setPlanTitle(effort.plan_title || '')
    setRunningSteps(effort.running_steps || [])
    setCurrentStepIndex(effort.current_step_index || 0)
    setRunProgress(effort.run_progress || 0)
    setClarification(effort.clarification || null)
    setStepError(effort.step_error || null)
    setFindings(effort.findings || [])
    setActions(effort.actions || [])
    setCompletionStats(effort.completion_stats || [])
    setExportResult(effort.export_result || null)
    setConversationHistory(effort.conversation_history || [])
    setOrchSessionId(effort.orchestrator_session_id || null)
    setOrchSessionId(effort.orchestrator_session_id || null)
  }

  // ─── Persist state changes ────────────────────────────────
  // One saver for the page's life: a change made while a save is in flight is queued
  // and sent after it, never dropped. A refused save is shown; a later success clears it.
  const [saver] = useState(() => createCoalescedSaver(
    async (id, stateUpdate) => {
      await updateWorkEffortState(id, stateUpdate)
      setSaveError(null)
    },
    (err) => {
      console.error('Failed to persist state:', err)
      setSaveError(saveErrorText(err))
    },
  ))
  const persistState = useCallback(
    (stateUpdate) => saver(effortId, stateUpdate),
    [saver, effortId],
  )

  // ─── Create work effort if needed, return ID ─────────────
  async function ensureEffortExists(title = 'New Work Effort') {
    if (effortId) return effortId
    try {
      const effort = await createWorkEffort(CUSTOMER_ID, title)
      const newId = effort.id
      setEffortId(newId)
      window.history.replaceState(
        null, '',
        `/customers/${CUSTOMER_ID}/work-efforts/${newId}`
      )
      return newId
    } catch (err) {
      console.error('Failed to create work effort:', err)
      return null
    }
  }

  // =================================================================
  // LOAD CUSTOMER & DATA SOURCES
  // =================================================================

  useEffect(() => {
    // Reset all customer-specific state when customer changes
    setCustomer(null)
    setCustomerLoading(true)
    setMessages([])
    setWorkState('empty')
    setPlanSteps([])
    setPlanTitle('')
    setRunningSteps([])
    setRunProgress(0)
    setCurrentStepIndex(0)
    setClarification(null)
    setStepError(null)
    setFindings([])
    setActions([])
    setCompletionStats([])
    setExportResult(null)
    setConversationHistory([])
    setOrchSessionId(null)
    setEffortId(EFFORT_ID_PARAM === 'new' ? null : EFFORT_ID_PARAM)
    setObservations([])
    setObservationsSummary(null)
    setObservationsError(null)
    setSelectedFinding(null)
    setSelectedDataSource(null)
    setSelectedProvenance(null)
    setObservationDrillCache({})
    prefillSentRef.current = false

    async function loadCustomer() {
      try {
        const ctx = await getCustomer(CUSTOMER_ID)
        setCustomer(ctx)
      } catch (err) {
        console.error('Failed to load customer:', err)
        setCustomer({
          customer_id: CUSTOMER_ID,
          company_name: 'Test Company',
          industry: 'Technology',
          employee_count: 500,
          hcm_vendor: 'UKG',
          hcm_product: 'UKG Pro',
        })
      } finally {
        setCustomerLoading(false)
      }
    }
    loadCustomer()
  }, [CUSTOMER_ID])

  // Reloaded after a pull from the Data Sources panel ends, so its tables and each
  // connection's last sync show what the pull did.
  const loadDataSources = useCallback(async () => {
    const [schemas, uploads, connections] = await Promise.allSettled([
      getSchemas(CUSTOMER_ID),
      getUploads(CUSTOMER_ID),
      getConnections(CUSTOMER_ID),
    ])
    setDataSources(dataSourcesFrom(schemas, uploads, connections))
  }, [CUSTOMER_ID])

  useEffect(() => {
    setDataSources({ connections: [], uploads: [], tables: [], errors: {} })
    loadDataSources()

    // Load observations (auto-findings)
    async function loadObservations() {
      setObservationsLoading(true)
      try {
        const result = await getObservations(CUSTOMER_ID)
        setObservations(result?.observations || [])
        setObservationsSummary(result?.summary || null)
        setObservationsError(null)
      } catch (err) {
        // said in the Findings tab, never shown as "no findings"
        setObservationsError(loadErrorMessage(err))
      } finally {
        setObservationsLoading(false)
      }
    }
    loadObservations()
  }, [CUSTOMER_ID])

  useEffect(() => {
    if (chatScrollRef.current) {
      chatScrollRef.current.scrollTop = chatScrollRef.current.scrollHeight
    }
  }, [messages, workState])

  useEffect(() => {
    const tabMap = {
      empty: 'plan', plan: 'plan', running: 'plan',
      clarification: 'plan', error: 'plan', complete: 'findings', export: 'export',
    }
    setActiveTab(tabMap[workState] || 'plan')
  }, [workState])

  // =================================================================
  // SEND MESSAGE
  // =================================================================

  const handleSend = useCallback(async (text) => {
    if (!text.trim() || chatLoading) return

    const title = extractTitle(text)
    const activeEffortId = await ensureEffortExists(title)

    const userMsg = { role: 'user', text, time: 'Just now' }
    setMessages(prev => [...prev, userMsg])
    setChatLoading(true)

    if (activeEffortId) {
      appendWorkEffortMessage(activeEffortId, { role: 'user', text, time: 'Just now' }).catch(console.error)
    }

    const historyForBackend = [
      ...conversationHistory,
      { role: 'user', content: text },
    ]

    try {
      const response = await chat(text, CUSTOMER_ID, {
        conversation_history: historyForBackend.map(h => ({ role: h.role, content: h.content })),
      })

      const newHistory = [
        ...historyForBackend,
        { role: 'assistant', content: response.answer || '' },
      ]
      setConversationHistory(newHistory)

      // ═══════════════════════════════════════════════════════
      // ROUTE BASED ON response_mode FROM BACKEND
      // Three explicit paths: clarification, investigation, direct_answer
      // ═══════════════════════════════════════════════════════
      const responseMode = response.response_mode || 'direct_answer'

      // ── CLARIFICATION ──────────────────────────────────────
      if (response.needs_clarification && response.clarification) {
        const clar = response.clarification
        const clarData = {
          question: clar.question || response.answer,
          options: (clar.options || []).map(o => ({
            label: typeof o === 'string' ? o : o.display || o.label || o.value,
            description: typeof o === 'string' ? '' : o.description || '',
            value: typeof o === 'string' ? o : o.value,
          })),
          stepLabel: clar.category || 'Clarification needed',
          clarificationKey: clar.clarification_key,
          key: clar.clarification_key,
          companyScope: response.metadata?.company_scope_carry || null,
          rememberLabel: response.metadata?.remember_label || null,
        }
        setClarification(clarData)
        const assistantMsg = {
          role: 'assistant',
          text: response.answer || 'I need some clarification before I can proceed.',
          time: 'Just now',
        }
        setMessages(prev => [...prev, assistantMsg])
        setWorkState('clarification')

        if (activeEffortId) {
          appendWorkEffortMessage(activeEffortId, assistantMsg).catch(console.error)
          persistState({ state: 'clarification', clarification: clarData, conversation_history: newHistory })
        }

      // ── FILE COMPARE UPLOAD (drop two files + map fields) ───
      } else if (responseMode === 'file_compare_upload') {
        const assistantMsg = { role: 'assistant', text: response.answer, time: 'Just now' }
        setMessages(prev => [...prev, assistantMsg])
        setCompareMeta(response.metadata?.compare || { customer_id: CUSTOMER_ID })
        if (activeEffortId) {
          appendWorkEffortMessage(activeEffortId, assistantMsg).catch(console.error)
          persistState({ conversation_history: newHistory })
        }

      // ── INVESTIGATION (orchestrator proposed a plan) ────────
      } else if (responseMode === 'investigation' && response.metadata?.approach) {
        const approach = response.metadata.approach
        const sessionId = response.metadata.session_id
        setOrchSessionId(sessionId)

        const stepTypeLabels = {
          document_extract: 'Document Search',
          config_lookup: 'Configuration Lookup',
          data_query: 'Data Query',
          compare: 'Comparison',
          compliance_check: 'Compliance Check',
          calculate: 'Calculation',
          synthesize: 'Synthesis',
          clarify: 'Clarification',
        }
        const steps = approach.steps.map(s => ({
          text: s.description,
          detail: s.method || `${stepTypeLabels[s.step_type] || s.step_type} · Step ${s.step_number}`,
          stepType: s.step_type,
          stepTypeLabel: stepTypeLabels[s.step_type] || s.step_type,
          orchStepId: s.id,
          requiresClarification: s.requires_clarification,
        }))

        const assistantMsg = { role: 'assistant', text: response.answer, time: 'Just now', raw: response }
        setMessages(prev => [...prev, assistantMsg])
        setPlanSteps(steps)
        setPlanTitle(title)
        setWorkState('plan')

        if (activeEffortId) {
          appendWorkEffortMessage(activeEffortId, {
            role: 'assistant', text: response.answer, time: 'Just now',
            request_id: response.request_id, raw: response,
          }).catch(console.error)
          persistState({
            state: 'plan', plan_steps: steps, plan_title: title, title,
            orchestrator_session_id: sessionId, conversation_history: newHistory,
          })
        }

      // ── ANALYSIS RESULT (compliance, variance, etc.) ────────
      } else if (response.is_analysis && response.analysis_result) {
        handleAnalysisResult(response, activeEffortId, newHistory)

      // ── DIRECT ANSWER ──────────────────────────────────────
      // Show the answer in chat. If there's data, store it for the right panel.
      // Do NOT force into plan/investigation flow.
      } else {
        const assistantMsg = {
          role: 'assistant',
          text: response.answer,
          time: 'Just now',
          raw: response,
          // Attach structured data to the message for right-panel display
          data: response.data,
          columns: response.columns,
          row_count: response.row_count,
          sql: response.sql,
          citations: response.citations,
        }
        setMessages(prev => [...prev, assistantMsg])

        if (activeEffortId) {
          appendWorkEffortMessage(activeEffortId, {
            role: 'assistant', text: response.answer, time: 'Just now',
            request_id: response.request_id, raw: response,
          }).catch(console.error)
          persistState({ conversation_history: newHistory })
        }

        // Stay in whatever state we're in — don't force a plan transition.
        // The user is in conversational mode. They can keep asking follow-ups
        // or ask a complex question that will trigger an investigation.
      }
    } catch (err) {
      console.error('Chat error:', err)
      const errorMsg = { role: 'assistant', text: `Something went wrong: ${err.message}. Please try again.`, time: 'Just now' }
      setMessages(prev => [...prev, errorMsg])
      if (activeEffortId) appendWorkEffortMessage(activeEffortId, errorMsg).catch(console.error)
    } finally {
      setChatLoading(false)
    }
  }, [chatLoading, workState, conversationHistory, effortId])

  function buildPlanFromResponse(response, question) {
    const steps = []
    if (response.data && response.row_count > 0) {
      steps.push({
        text: `Query: ${question}`,
        detail: `${response.row_count} rows returned · ${response.processing_time_ms?.toFixed(0)}ms`,
        status: 'complete',
        statusText: response.answer,
      })
      return steps
    }
    steps.push(
      { text: 'Analyze the question and identify required data sources', detail: 'Intent + Term engines' },
      { text: 'Query customer data to gather relevant information', detail: `Against ${dataSources.tables.length} tables` },
      { text: 'Cross-reference against compliance rules and product capabilities', detail: 'Five Truths analysis' },
      { text: 'Identify gaps, risks, and variances', detail: 'Gap analysis engine' },
      { text: 'Generate findings with provenance citations', detail: 'All evidence tracked' },
      { text: 'Synthesize recommendations and action items', detail: 'Prioritized by severity' },
    )
    return steps
  }

  function extractTitle(question) {
    if (question.length <= 40) return question
    return question.split(' ').slice(0, 6).join(' ') + '…'
  }

  function handleCompareComplete(res) {
    const newFindings = []
    const matchPct = Math.round((res.match_rate || 0) * 100)
    newFindings.push({
      type: matchPct >= 95 ? 'compatible' : 'warning',
      title: `${matchPct}% match rate · ${(res.variance_count || 0).toLocaleString()} variances`,
      detail: `${(res.match_count || 0).toLocaleString()} of ${(res.source_rows || 0).toLocaleString()} records matched between ${res.source_file} and ${res.target_file}.`,
      source: `${res.source_file} × ${res.target_file} (key: ${res.key_field})`,
    })
    if (res.missing_in_target) newFindings.push({ type: 'warning', title: `${res.missing_in_target.toLocaleString()} records missing in target`, detail: 'Present in source, absent in target.', source: res.target_file })
    if (res.missing_in_source) newFindings.push({ type: 'warning', title: `${res.missing_in_source.toLocaleString()} records missing in source`, detail: 'Present in target, absent in source.', source: res.source_file })
    ;(res.patterns || []).forEach(p => newFindings.push({
      type: 'warning',
      title: p.description || 'Pattern detected',
      detail: p.root_cause ? `Likely cause: ${p.root_cause}` : `Affects ${(p.affected_count || 0).toLocaleString()} records`,
      source: 'Pattern analysis',
    }))
    ;(res.variances || []).slice(0, 10).forEach(v => {
      const sig = (v.fields || []).some(f => f.variance_type === 'significant')
      newFindings.push({
        type: sig ? 'critical' : 'compatible',
        title: `Variance on ${v.key}`,
        detail: (v.fields || []).map(f => `${f.field}: ${f.source} → ${f.target}`).join('; '),
        source: `key: ${res.key_field}`,
      })
    })

    const newActions = (res.recommendations || []).map(rec => ({
      title: rec, detail: 'From comparison analysis',
      tags: [{ label: 'Recommendation', color: 'amber' }], hours: '~2 hrs', done: false,
    }))

    const allFindings = [...findings, ...newFindings]
    const allActions = [...actions, ...newActions]
    setFindings(allFindings)
    setActions(allActions)
    const stats = [
      { value: newFindings.filter(f => f.type === 'critical').length, label: 'Critical', type: 'critical' },
      { value: newFindings.filter(f => f.type === 'warning').length, label: 'Warnings', type: 'warnings' },
      { value: newFindings.filter(f => f.type === 'compatible').length, label: 'Compatible', type: 'compatible' },
      { value: newFindings.filter(f => f.type === 'blocked').length, label: 'Blocked', type: 'blocked' },
    ]
    setCompletionStats(stats)
    setActiveTab('findings')
    setWorkState('complete')
    persistState({ state: 'complete', findings: allFindings, actions: allActions, completion_stats: stats })
  }

  function handleAnalysisResult(response, activeEffortId, newHistory) {
    const result = response.analysis_result
    const newFindings = []
    const newActions = []

    if (result.variances) {
      result.variances.forEach(v => {
        const severity = v.severity || (v.difference_pct > 10 ? 'critical' : v.difference_pct > 5 ? 'warning' : 'compatible')
        newFindings.push({
          type: severity,
          title: v.field ? `${v.field} variance: ${v.source_value} → ${v.target_value}` : v.description || 'Variance detected',
          detail: v.explanation || `Difference: ${v.difference || v.difference_pct + '%'}`,
          source: v.provenance || `${v.source_table || 'source'} × ${v.target_table || 'target'}`,
        })
      })
    }

    if (result.patterns) {
      result.patterns.forEach(p => {
        newFindings.push({ type: 'warning', title: p.pattern || p.description, detail: `Affects ${p.count || '?'} records`, source: p.source || 'Pattern analysis' })
      })
    }

    newFindings.filter(f => f.type === 'critical' || f.type === 'warning').forEach(f => {
      newActions.push({
        title: `Remediate: ${f.title}`, detail: f.detail,
        tags: [{ label: f.type === 'critical' ? 'Critical' : 'Warning', color: f.type === 'critical' ? 'red' : 'amber' }],
        hours: f.type === 'critical' ? '~4 hrs' : '~2 hrs', done: false,
      })
    })

    const allFindings = [...findings, ...newFindings]
    const allActions = [...actions, ...newActions]
    setFindings(allFindings)
    setActions(allActions)

    const stats = [
      { value: newFindings.filter(f => f.type === 'critical').length, label: 'Critical', type: 'critical' },
      { value: newFindings.filter(f => f.type === 'warning').length, label: 'Warnings', type: 'warnings' },
      { value: newFindings.filter(f => f.type === 'compatible').length, label: 'Compatible', type: 'compatible' },
      { value: newFindings.filter(f => f.type === 'blocked').length, label: 'Blocked', type: 'blocked' },
    ]
    setCompletionStats(stats)

    const assistantMsg = { role: 'assistant', text: response.answer || `Analysis complete. Found ${newFindings.length} items.`, time: 'Just now', raw: response }
    setMessages(prev => [...prev, assistantMsg])
    setWorkState('complete')

    if (activeEffortId) {
      appendWorkEffortMessage(activeEffortId, {
        role: 'assistant', text: response.answer || `Analysis complete. Found ${newFindings.length} items.`,
        time: 'Just now', request_id: response.request_id, raw: response,
      }).catch(console.error)
      persistState({ state: 'complete', findings: allFindings, actions: allActions, completion_stats: stats, conversation_history: newHistory })
    }
  }

  // ─── RUN INVESTIGATION ──────────────────────────────────
  const handleRunInvestigation = useCallback(async () => {
    // Immediate feedback in chat
    setMessages(prev => [...prev, { role: 'assistant', text: '▶ Starting investigation… Approving plan and executing steps.', time: 'Just now' }])
    
    setWorkState('running')
    const steps = planSteps.map((s, i) => ({
      ...s,
      status: i === 0 ? 'running' : 'pending',
      statusText: i === 0 ? 'Queuing for execution…' : undefined,
    }))
    setRunningSteps(steps)
    setRunProgress(0)
    persistState({ state: 'running', running_steps: steps, run_progress: 0 })

    // ── Orchestrator path: approve approach and poll for progress ──
    // P1-1: migrated to POST /api/work-efforts/{id}/run.
    // /run approve is fire-and-forget; /run refresh is the poll.
    // Backend persists running_steps, findings, completion_stats to
    // Supabase automatically, so a mid-investigation page reload no
    // longer loses state.
    if (orchSessionId && effortId) {
      try {
        // Kick off execution — returns in ~1s with state=running
        await runWorkEffort(effortId, 'approve')

        // Poll /run refresh every 3 seconds for up to 6 minutes
        const pollInterval = 3000
        const maxPolls = 120
        let polls = 0

        const poll = async () => {
          polls++
          if (polls > maxPolls) {
            setStepError({ title: 'Investigation timed out', text: 'The analysis took too long. Try a simpler question or break it into parts.', code: 'TIMEOUT' })
            setWorkState('error')
            persistState({ state: 'error', step_error: { title: 'Timeout', text: 'Investigation exceeded time limit', code: 'TIMEOUT' } })
            return
          }

          try {
            // /run refresh returns the full Work Effort row
            const we = await runWorkEffort(effortId, 'refresh')

            // Adapt the Work Effort shape to the existing approach-shape
            // the rest of this function expects. Minimizes churn.
            const session = {
              knowledge_sources: we.knowledge_sources || [],
              status: we.state === 'running' ? 'in_progress' :
                      we.state === 'completed' ? 'completed' :
                      we.state === 'waiting_clarification' ? 'waiting_clarification' :
                      we.state === 'error' ? 'failed' : 'in_progress',
            }
            const approach = {
              steps: we.running_steps || [],
              current_step: we.current_step_index,
              status: session.status,
            }

            // Update step statuses from orchestrator
            if (approach.steps) {
              const updatedSteps = planSteps.map((planStep, i) => {
                const orchStep = approach.steps[i]
                if (!orchStep) return { ...planStep, status: 'pending' }
                const statusMap = { completed: 'complete', running: 'running', pending: 'pending', failed: 'error', waiting_clarification: 'paused', skipped: 'complete' }
                return {
                  ...planStep,
                  status: statusMap[orchStep.status] || orchStep.status,
                  statusText: orchStep.status === 'completed' && orchStep.result
                    ? `Complete${orchStep.engine_used ? ' — via ' + orchStep.engine_used : ''}`
                    : orchStep.status === 'failed' ? `Failed: ${orchStep.error || 'Unknown error'}`
                    : orchStep.status === 'running' ? 'Processing…'
                    : undefined,
                }
              })
              setRunningSteps(updatedSteps)

              const completed = updatedSteps.filter(s => s.status === 'complete').length
              const progress = Math.round((completed / updatedSteps.length) * 100)
              setRunProgress(progress)
              setCurrentStepIndex(approach.current_step || completed)
            }

            // Check overall status
            const status = approach.status || session.status
            if (status === 'completed') {
              setRunProgress(100)
              setRunningSteps(prev => prev.map(s => ({ ...s, status: 'complete' })))

              // ═══════════════════════════════════════════════════════
              // CAPTURE KNOWLEDGE SOURCES for provenance
              // ═══════════════════════════════════════════════════════
              const sessionKnowledgeSources = session.knowledge_sources || []
              if (sessionKnowledgeSources.length > 0) {
                setKnowledgeSources(sessionKnowledgeSources)
              }

              // ═══════════════════════════════════════════════════════
              // USE FINDINGS/STATS the backend already computed via /run
              // The backend's _extract_findings and _compute_completion_stats
              // have already done this work and persisted it to Supabase.
              // Prefer those over re-deriving on the client.
              // ═══════════════════════════════════════════════════════
              const backendFindings = we.findings || []
              const backendStats = we.completion_stats || []
              if (backendFindings.length > 0) {
                setFindings(backendFindings)
              }
              if (backendStats.length > 0) {
                setCompletionStats(backendStats)
              }

              // Fall through to legacy client-side extraction only if the
              // backend returned no findings — shouldn't happen for fresh
              // runs, but preserves behavior for older sessions.
              const newFindings = []
              const newActions = []
              const steps = approach.steps || []

              // Collect all per-step provenance sources (from document_extract steps)
              const stepProvenance = []
              for (const step of steps) {
                if (step.result?.provenance_sources) {
                  stepProvenance.push(...step.result.provenance_sources)
                }
              }

              for (const step of steps) {
                const result = step.result || {}
                const sql = step.sql_executed || result.sql || null

                // Data query steps — surface what was queried
                if (step.step_type === 'data_query' && result.data && result.row_count > 0) {
                  newFindings.push({
                    type: 'compatible',
                    title: `Data retrieved: ${result.row_count} records`,
                    detail: step.description,
                    source: sql ? `SQL: ${sql.substring(0, 120)}…` : step.engine_used || 'sql_engine',
                    data: result.data,
                    sql: sql,
                    rowCount: result.row_count,
                    provenance: [{ type: 'sql_query', content: sql, metadata: { tables: result.tables_used || [], row_count: result.row_count } }],
                  })
                }

                // Document extract steps — surface what was found with provenance
                if (step.step_type === 'document_extract' && result.provenance_sources?.length > 0) {
                  newFindings.push({
                    type: 'compatible',
                    title: `Knowledge retrieved: ${result.document_count} documents`,
                    detail: step.description,
                    source: 'Knowledge base',
                    provenance: result.provenance_sources,
                  })
                }

                // Calculate steps — surface division-level stats with compa-ratios
                if (step.step_type === 'calculate' && result.data && result.data.length > 0) {
                  // Show company summary first
                  if (result.company_summary) {
                    const cs = result.company_summary
                    newFindings.push({
                      type: 'compatible',
                      title: `Company profile: ${cs.total_employees} employees, ${cs.total_divisions} divisions`,
                      detail: `Avg salary $${cs.company_avg_salary?.toLocaleString()}, median $${cs.company_median_salary?.toLocaleString()}`,
                      source: 'Compensation calculation',
                      data: result.data,
                      sql: sql,
                    })
                  }
                  
                  // Show division stats
                  for (const row of result.data.slice(0, 10)) {
                    const div = row.division || row.department || 'Unknown'
                    const avg = row.avg_salary
                    const med = row.median_salary
                    const hc = row.headcount
                    const compa = row.compa_ratio
                    const spread = row.range_spread_pct
                    
                    if (avg && div) {
                      const compaLabel = compa ? ` · compa-ratio ${compa}` : ''
                      const p25 = row.p25 ? `P25: $${row.p25?.toLocaleString()}` : ''
                      const p75 = row.p75 ? `P75: $${row.p75?.toLocaleString()}` : ''
                      const percentiles = p25 && p75 ? ` · ${p25} / ${p75}` : ''
                      
                      newFindings.push({
                        type: 'compatible',
                        title: `${div}: avg $${avg?.toLocaleString()}, median $${med?.toLocaleString()}${compaLabel}`,
                        detail: `${hc} employees · range $${row.min_salary?.toLocaleString()} – $${row.max_salary?.toLocaleString()} (${spread}% spread)${percentiles}`,
                        source: 'Division analysis',
                      })
                    }
                  }
                }

                // Compare steps — surface structured findings from the new compare engine
                if (step.step_type === 'compare' && result.findings) {
                  for (const f of result.findings) {
                    const category = f.category || ''
                    
                    // Map finding types and categories to UI severity
                    let type = f.type || 'info'
                    if (type === 'info') type = 'compatible' // info → compatible in our UI
                    
                    // Build title based on category
                    let title = f.description || ''
                    if (title.length > 120) title = title.substring(0, 117) + '…'
                    
                    // Skip pure info/summary items from cluttering findings
                    if (category === 'outlier_summary' || category === 'compression_summary') continue
                    
                    let source = 'Equity analysis'
                    if (category.includes('outlier')) source = 'Individual outlier analysis'
                    else if (category.includes('compression')) source = 'Salary compression check'
                    else if (category.includes('division')) source = 'Division comparison'
                    else if (category.includes('range')) source = 'Range spread analysis'
                    
                    newFindings.push({ type, title, detail: '', source })
                  }
                }

                // Compliance check steps — surface issues
                if (step.step_type === 'compliance_check' && result.findings) {
                  for (const f of result.findings) {
                    newFindings.push({
                      type: f.severity === 'high' ? 'critical' : f.severity === 'medium' ? 'warning' : 'compatible',
                      title: f.analysis || f.description || 'Compliance finding',
                      detail: f.recommendation || '',
                      source: `Compliance: ${result.compliance_type || 'general'}`,
                    })
                  }
                }
              }

              // ═══════════════════════════════════════════════════════
              // ATTACH SYNTHESIS-LEVEL PROVENANCE to all findings
              // If we have session-level knowledge sources AND per-step
              // provenance, merge them as available context for all findings
              // ═══════════════════════════════════════════════════════
              const allProvenance = [...stepProvenance, ...sessionKnowledgeSources]
              if (allProvenance.length > 0) {
                for (const f of newFindings) {
                  if (!f.provenance || f.provenance.length === 0) {
                    // Findings without their own provenance get the session-level sources
                    f.provenance = sessionKnowledgeSources
                  }
                }
              }

              // Generate actions from critical/warning findings — use source to categorize
              newFindings.filter(f => f.type === 'critical' || f.type === 'warning').forEach(f => {
                let actionTitle, actionDetail, hours
                const src = (f.source || '').toLowerCase()
                
                if (src.includes('outlier') && f.title.includes('above')) {
                  actionTitle = `Review overpaid outlier: ${f.title.split('(')[0].trim()}`
                  actionDetail = `Employee compensation is statistically high for their division. Verify role scope, market data, or reclassification.`
                  hours = '~2 hrs'
                } else if (src.includes('outlier') && f.title.includes('below')) {
                  actionTitle = `Review underpaid outlier: ${f.title.split('(')[0].trim()}`
                  actionDetail = `Employee compensation is statistically low. Assess equity risk, flight risk, and cost-to-bring-to-band.`
                  hours = '~2 hrs'
                } else if (src.includes('compression')) {
                  actionTitle = `Address salary compression: ${f.title.split(':')[0]}`
                  actionDetail = `Senior roles earning near-parity with junior roles. Evaluate market adjustment or role reclassification to restore differentiation.`
                  hours = '~4 hrs'
                } else if (src.includes('range spread')) {
                  actionTitle = `Tighten compensation bands: ${f.title.split(':')[0]}`
                  actionDetail = `Wide range spread suggests inconsistent pay practices. Define formal salary bands and identify employees outside range.`
                  hours = '~6 hrs'
                } else if (src.includes('division') && f.title.includes('equity gap')) {
                  actionTitle = `Investigate division pay equity gap`
                  actionDetail = `Significant disparity between divisions may indicate structural bias or outdated compensation philosophy. Conduct market benchmarking.`
                  hours = '~8 hrs'
                } else {
                  actionTitle = `Investigate: ${f.title.substring(0, 80)}`
                  actionDetail = f.detail || f.title
                  hours = f.type === 'critical' ? '~4 hrs' : '~2 hrs'
                }
                newActions.push({
                  title: actionTitle,
                  detail: actionDetail,
                  tags: [{ label: f.type === 'critical' ? 'Critical' : 'Warning', color: f.type === 'critical' ? 'red' : 'amber' }],
                  hours,
                  done: false,
                })
              })

              setFindings(prev => [...prev, ...newFindings])
              setActions(prev => [...prev, ...newActions])

              const stats = [
                { value: newFindings.filter(f => f.type === 'critical').length, label: 'Critical', type: 'critical' },
                { value: newFindings.filter(f => f.type === 'warning').length, label: 'Warnings', type: 'warnings' },
                { value: newFindings.filter(f => f.type === 'compatible').length, label: 'Compatible', type: 'compatible' },
                { value: newFindings.filter(f => f.type === 'blocked').length, label: 'Blocked', type: 'blocked' },
              ]
              setCompletionStats(stats)

              // Store approach steps and narrative for export
              setOrchApproachSteps(steps)

              setWorkState('complete')

              // Extract narrative as completion message
              const narrative = session.final_narrative || session.narrative
              if (narrative) {
                setFinalNarrative(narrative)
                const msg = { role: 'assistant', text: narrative, time: 'Just now' }
                setMessages(prev => [...prev, msg])
                if (effortId) appendWorkEffortMessage(effortId, msg).catch(console.error)
              }

              persistState({
                state: 'complete',
                running_steps: planSteps.map(s => ({ ...s, status: 'complete' })),
                run_progress: 100,
                findings: newFindings,
                actions: newActions,
                completion_stats: stats,
              })
              return
            }

            if (status === 'failed') {
              const errorInfo = { title: 'Investigation failed', text: session.error || 'The orchestrator encountered an error.', code: session.error || 'ORCH_FAIL' }
              setStepError(errorInfo)
              setWorkState('error')
              persistState({ state: 'error', step_error: errorInfo })
              return
            }

            if (status === 'paused' || status === 'waiting_clarification') {
              // Find the step that needs clarification
              const waitingStep = (approach.steps || []).find(s => s.status === 'waiting_clarification')
              if (waitingStep && waitingStep.clarification) {
                const clarData = {
                  question: waitingStep.clarification.question,
                  options: [
                    { label: 'Provide answer', description: waitingStep.clarification.context || '', value: waitingStep.clarification.concept || '' },
                  ],
                  stepLabel: `Step ${waitingStep.step_number} · ${waitingStep.clarification.concept || 'Clarification'}`,
                  orchConcept: waitingStep.clarification.concept,
                }
                setClarification(clarData)
                setRunningSteps(prev => prev.map((s, i) => ({
                  ...s, 
                  status: i < waitingStep.step_number - 1 ? 'complete' : i === waitingStep.step_number - 1 ? 'paused' : 'pending',
                  statusText: i === waitingStep.step_number - 1 ? 'Paused — needs your input' : s.statusText,
                })))
                setWorkState('clarification')
                persistState({ state: 'clarification', clarification: clarData })
                return
              }
            }

            // Still in progress — poll again
            setTimeout(poll, pollInterval)
          } catch (err) {
            console.error('Poll error:', err)
            // Retry on transient errors
            if (polls < maxPolls) setTimeout(poll, pollInterval * 2)
          }
        }

        // Start polling
        setTimeout(poll, pollInterval)
        return

      } catch (err) {
        console.error('Orchestrator approve failed:', err)
        const errorInfo = {
          title: 'Investigation failed to start',
          text: `The orchestrator could not approve the plan: ${err.message}`,
          code: err.message,
        }
        setStepError(errorInfo)
        setWorkState('error')
        persistState({ state: 'error', step_error: errorInfo })
        return
      }
    }

    // No orchestrator session — this shouldn't happen since investigations
    // always go through the orchestrator. Show an explicit error.
    const errorInfo = {
      title: 'No investigation session',
      text: 'This plan was not created by the investigation engine. Please ask your question again to generate a new plan.',
      code: 'NO_ORCH_SESSION',
    }
    setStepError(errorInfo)
    setWorkState('error')
    persistState({ state: 'error', step_error: errorInfo })

    setRunProgress(100)
    const completedSteps = steps.map(s => ({ ...s, status: 'complete' }))
    setRunningSteps(completedSteps)
    setWorkState('complete')
    persistState({ state: 'complete', running_steps: completedSteps, run_progress: 100 })
  }, [planSteps, conversationHistory, effortId])

  // ─── Clarification Response ─────────────────────────────
  const handleClarificationSubmit = useCallback(async (selectedIndex, remember) => {
    if (!clarification) return
    const selected = clarification.options[selectedIndex]
    if (!selected) return
    setChatLoading(true)

    try {
      let assistantMsg

      // If this is an orchestrator clarification, use /run clarify
      // (migrated from submitOrchClarification in P1-1 — same effect, but
      // /run persists the updated state into the work_efforts row).
      if (orchSessionId && clarification.orchConcept && effortId) {
        await runWorkEffort(effortId, 'clarify', {
          clarification_response: {
            [clarification.orchConcept]: selected.value || selected.label,
          },
        })
        assistantMsg = { role: 'assistant', text: `Got it — continuing the investigation.`, time: 'Just now' }
      } else {
        // Get the original question from conversation history
        const originalQuestion = conversationHistory.filter(h => h.role === 'user').pop()?.content || ''
        const response = await chat(originalQuestion, CUSTOMER_ID, {
          clarification_key: clarification.clarificationKey || clarification.key || 'active_employees',
          clarification_value: selected.value || selected.label,
          clarification_remember: Boolean(remember),
          ...(clarification.companyScope ? { settled_company_scope: clarification.companyScope } : {}),
          conversation_history: conversationHistory.map(h => ({ role: h.role, content: h.content })),
        })

        // Check if the response is ANOTHER clarification (chained clarifications)
        if (response.needs_clarification && response.clarification) {
          const clar2 = response.clarification
          const clarData2 = {
            question: clar2.question || response.answer,
            options: (clar2.options || []).map(o => ({
              label: typeof o === 'string' ? o : o.display || o.label || o.value,
              description: typeof o === 'string' ? '' : o.description || '',
              value: typeof o === 'string' ? o : o.value,
            })),
            stepLabel: clar2.category || 'Clarification needed',
            clarificationKey: clar2.clarification_key,
            key: clar2.clarification_key,
            companyScope: response.metadata?.company_scope_carry || null,
            rememberLabel: response.metadata?.remember_label || null,
          }
          setClarification(clarData2)
          assistantMsg = {
            role: 'assistant',
            text: response.answer || 'I need one more clarification before I can proceed.',
            time: 'Just now',
          }
          setMessages(prev => [...prev, assistantMsg])
          setWorkState('clarification')
          if (effortId) {
            appendWorkEffortMessage(effortId, assistantMsg).catch(console.error)
            persistState({ state: 'clarification', clarification: clarData2 })
          }
          setChatLoading(false)
          return
        }

        assistantMsg = {
          role: 'assistant',
          text: response.answer || 'Got it, continuing…',
          time: 'Just now',
          raw: response,
          data: response.data,
          columns: response.columns,
          row_count: response.row_count,
          sql: response.sql,
          citations: response.citations,
        }
      }

      setMessages(prev => [...prev, assistantMsg])
      if (effortId) appendWorkEffortMessage(effortId, assistantMsg).catch(console.error)

      setClarification(null)

      if (runningSteps.length > 0) {
        setWorkState('running')
        const pausedIdx = runningSteps.findIndex(s => s.status === 'paused')
        if (pausedIdx >= 0) {
          const resumed = runningSteps.map((s, idx) => ({
            ...s, status: idx <= pausedIdx ? 'complete' : idx === pausedIdx + 1 ? 'running' : 'pending',
            statusText: idx === pausedIdx ? 'Complete (after clarification)' : s.statusText,
          }))
          setRunningSteps(resumed)
          persistState({ state: 'running', running_steps: resumed, clarification: null })
        }
      } else {
        // If the response has data/sql, this was a direct answer — show results panel
        if (assistantMsg.data || assistantMsg.sql) {
          setWorkState('empty')
          persistState({ state: 'empty', clarification: null })
        } else {
          setWorkState('plan')
          persistState({ state: 'plan', clarification: null })
        }
      }
    } catch (err) {
      console.error('Clarification error:', err)
    } finally {
      setChatLoading(false)
    }
  }, [clarification, runningSteps, conversationHistory, effortId])

  // ─── Error Actions ──────────────────────────────────────
  const handleRetryStep = useCallback(() => {
    setStepError(null)
    setWorkState('running')
    persistState({ state: 'running', step_error: null })
  }, [effortId])

  const handleSkipStep = useCallback(() => {
    setStepError(null)
    const skipped = runningSteps.map(s => s.status === 'error' ? { ...s, status: 'complete', statusText: 'Skipped' } : s)
    setRunningSteps(skipped)
    setWorkState('running')
    persistState({ state: 'running', step_error: null, running_steps: skipped })
  }, [runningSteps, effortId])

  // ─── Export ─────────────────────────────────────────────
  const handleExport = useCallback(async (templateId, options) => {
    try {
      // Use work-effort export endpoint which generates from findings/actions
      const API_BASE = API_BASE_CONFIG
      const response = await fetch(`${API_BASE}/api/export/work-effort`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          customer_id: CUSTOMER_ID,
          customer_name: customer?.company_name || CUSTOMER_ID,
          title: planTitle || 'Work Effort Analysis',
          findings,
          actions,
          steps: orchApproachSteps,
          narrative: finalNarrative,
          include_provenance: options.provenance !== false,
          include_raw_data: options.samples === true,
          knowledge_sources: knowledgeSources,
        }),
      })
      const result = await response.json()

      let exportData
      if (result.success && (result.export_id || result.download_url)) {
        exportData = {
          exportId: result.export_id,
          downloadUrl: `${API_BASE}${result.download_url}`,
          status: 'complete',
          filename: result.filename,
          sheetCount: result.sheet_count,
          sheetDetails: result.sheet_details,
          totalRows: result.total_rows,
        }
      } else {
        exportData = { error: result.detail || result.error || 'Export returned no data' }
      }
      setExportResult(exportData)
      setWorkState('export')
      persistState({ state: 'export', export_result: exportData })
    } catch (err) {
      console.error('Export error:', err)
      const exportData = { error: err.message || 'Export failed.' }
      setExportResult(exportData)
      setWorkState('export')
      persistState({ state: 'export', export_result: exportData })
    }
  }, [effortId, findings, actions, orchApproachSteps, finalNarrative, customer, planTitle, knowledgeSources])

  // ─── Computed ───────────────────────────────────────────
  const breadcrumbTitle = workState === 'empty' ? 'New Work Effort' : (planTitle || 'Work Effort')
  const breadcrumbs = [
    { label: 'Customers', href: '/customers' },
    { label: customer?.company_name || 'Loading…', href: `/customers/${CUSTOMER_ID}` },
    { label: breadcrumbTitle },
  ]

  // ─── Two-tier Suggestions (customer-aware) ───────────────────
  const simpleQueries = useMemo(() => {
    const name = customer?.company_name || 'this customer'
    return [
      'How many active employees are there?',
      'List all states with employees',
      'Show me pay frequencies',
      'What departments exist?',
      'Count employees by status',
    ]
  }, [customer])

  const complexQueries = useMemo(() => {
    const chips = []
    const systems = customer?.systems || []
    const sourceSystem = systems.find(s => s.status === 'decommissioning' || s.status === 'legacy')
    const targetSystem = systems.find(s => s.status === 'active' || s.is_primary)
    const projectType = customer?.current_project_type || ''

    if (projectType === 'migration' && sourceSystem && targetSystem) {
      chips.push({ icon: '🔍', text: `Compare ${sourceSystem.vendor_name} vs ${targetSystem.vendor_name} payroll configuration` })
      chips.push({ icon: '⚠️', text: `Identify earning codes in ${sourceSystem.vendor_name} with no ${targetSystem.vendor_name} equivalent` })
    }
    if (projectType === 'implementation' && targetSystem) {
      chips.push({ icon: '✅', text: `Check ${targetSystem.product_name} readiness for go-live` })
    }

    chips.push({ icon: '📋', text: 'Audit FLSA classification against salary thresholds' })
    chips.push({ icon: '🏛️', text: 'Run compliance check across all jurisdictions' })
    chips.push({ icon: '📊', text: 'Run a full migration gap analysis with remediation plan' })

    return chips.slice(0, 4)
  }, [customer])

  // ─── Step Edit/Delete Handlers ──────────────────────────
  const [regenerating, setRegenerating] = useState(false)

  const handleEditStep = useCallback((index, newText) => {
    setPlanSteps(prev => prev.map((s, i) => i === index ? { ...s, text: newText, detail: '⟳ Method will regenerate before running…', needsRegen: true } : s))
  }, [])

  const handleDeleteStep = useCallback((index) => {
    setPlanSteps(prev => prev.filter((_, i) => i !== index))
  }, [])

  const handleAddStep = useCallback(() => {
    setPlanSteps(prev => [...prev, { text: 'New step — click edit to describe', detail: '⟳ Method will regenerate before running…', stepType: 'data_query', stepTypeLabel: 'Data Query', needsRegen: true }])
  }, [])

  // Regenerate methods for edited/added steps by sending them back to LLM
  const regenerateMethods = useCallback(async (steps) => {
    setRegenerating(true)
    try {
      const { chat: chatApi } = await import('../../api')
      // Build a prompt asking the LLM to fill in methods for the given step descriptions
      const stepsText = steps.map((s, i) => `Step ${i + 1} [${s.stepType || 'data_query'}]: ${s.text}`).join('\n')
      const response = await chatApi(
        `Given this investigation plan, provide a JSON array of "method" strings explaining HOW each step works (tables, joins, calculations). Steps:\n${stepsText}`,
        CUSTOMER_ID,
        { mode: 'orchestrate_regen' }
      )
      // Try to parse methods from response
      const answer = response?.answer || response?.response || ''
      try {
        const jsonMatch = answer.match(/\[[\s\S]*\]/)
        if (jsonMatch) {
          const methods = JSON.parse(jsonMatch[0])
          return steps.map((s, i) => ({
            ...s,
            detail: methods[i] || s.detail,
            needsRegen: false,
          }))
        }
      } catch (parseErr) {
        console.warn('Method regen parse failed, using descriptions as-is', parseErr)
      }
    } catch (err) {
      console.error('Method regeneration failed:', err)
    } finally {
      setRegenerating(false)
    }
    // Fallback — clear the regen flag, keep whatever detail they have
    return steps.map(s => ({ ...s, needsRegen: false }))
  }, [CUSTOMER_ID])

  // ─── Data Source Preview ──────────────────────────────────
  const handleDataSourceClick = useCallback(async (tableName, displayName) => {
    // Clear finding selection if any
    setSelectedFinding(null)
    setSelectedDataSource({ tableName, displayName, loading: true, data: null, error: null })
    try {
      const result = await getTablePreview(CUSTOMER_ID, tableName, 100, 0)
      setSelectedDataSource(prev => prev?.tableName === tableName ? {
        ...prev,
        loading: false,
        data: result.data || [],
        columns: result.columns || [],
        columnTypes: result.column_types || {},
        totalRows: result.total_rows || 0,
        hasMore: result.has_more || false,
        limit: result.limit || 100,
        offset: result.offset || 0,
      } : prev)
    } catch (err) {
      setSelectedDataSource(prev => prev?.tableName === tableName ? {
        ...prev, loading: false, error: err.message || 'Failed to load'
      } : prev)
    }
  }, [CUSTOMER_ID])

  const handleDataSourceLoadMore = useCallback(async (newLimit) => {
    if (!selectedDataSource?.tableName) return
    const tableName = selectedDataSource.tableName
    setSelectedDataSource(prev => ({ ...prev, loading: true }))
    try {
      const result = await getTablePreview(CUSTOMER_ID, tableName, newLimit || 100, 0)
      setSelectedDataSource(prev => prev?.tableName === tableName ? {
        ...prev,
        loading: false,
        data: result.data || [],
        columns: result.columns || [],
        totalRows: result.total_rows || 0,
        hasMore: result.has_more || false,
        limit: result.limit || 100,
        offset: 0,
      } : prev)
    } catch (err) {
      setSelectedDataSource(prev => ({ ...prev, loading: false, error: err.message }))
    }
  }, [CUSTOMER_ID, selectedDataSource?.tableName])

  // ─── PROVENANCE CLICK ────────────────────────────────────
  const handleProvenanceClick = useCallback((provenanceSource) => {
    setSelectedFinding(null)
    setSelectedDataSource(null)
    setSelectedProvenance(provenanceSource)
  }, [])

  return (
    <div className="layout-fixed-viewport">
      <TopNav breadcrumbs={breadcrumbs} />
      <ContextBar customer={customer} dataSources={dataSources} />
      {saveError && (
        <div className="we-save-error-banner" role="alert">
          <span>{saveError}</span>
          <button className="we-save-error-dismiss" onClick={() => setSaveError(null)}>Dismiss</button>
        </div>
      )}

      <div className="workspace-body">
        {/* ── LEFT: CHAT or DATA VIEWER or PROVENANCE VIEWER ── */}
        <div className="chat-panel">
          {/* Provenance viewer — show source chunk + metadata */}
          {selectedProvenance ? (
            <div className="data-viewer">
              <div className="dv-header">
                <div className="dv-header-left">
                  <h3 className="dv-title">{selectedProvenance.metadata?.file_name || 'Source Document'}</h3>
                  <p className="dv-subtitle">
                    {selectedProvenance.metadata?.truth_type && <span className="prov-truth-badge">{selectedProvenance.metadata.truth_type.replace('truth_', 'Truth ').replace('_', ' ')}</span>}
                    {selectedProvenance.metadata?.functional_area && ` · ${selectedProvenance.metadata.functional_area}`}
                    {selectedProvenance.metadata?.vendor_id && selectedProvenance.metadata.vendor_id !== 'unknown' && ` · ${selectedProvenance.metadata.vendor_id}`}
                    {selectedProvenance.distance != null && ` · Relevance: ${Math.round((1 - selectedProvenance.distance) * 100)}%`}
                  </p>
                </div>
                <button className="dv-close" onClick={() => setSelectedProvenance(null)}>✕ Close</button>
              </div>
              <div className="prov-viewer-body">
                <div className="prov-meta-strip">
                  {selectedProvenance.metadata?.document_type && <span className="prov-meta-tag">{selectedProvenance.metadata.document_type}</span>}
                  {selectedProvenance.metadata?.jurisdiction_type && <span className="prov-meta-tag">{selectedProvenance.metadata.jurisdiction_type}</span>}
                  {selectedProvenance.hierarchy_level && <span className="prov-meta-tag">{selectedProvenance.hierarchy_level}</span>}
                  {selectedProvenance.metadata?.chunk_index != null && (
                    <span className="prov-meta-tag">Chunk {selectedProvenance.metadata.chunk_index + 1} of {selectedProvenance.metadata.total_chunks || '?'}</span>
                  )}
                </div>
                <div className="prov-content">
                  {selectedProvenance.content || 'No content available'}
                </div>
                {selectedProvenance.type === 'sql_query' && selectedProvenance.content && (
                  <pre className="prov-sql">{selectedProvenance.content}</pre>
                )}
                {selectedProvenance.metadata?.base_doc_id && (
                  <div className="prov-context-hint">
                    📄 Part of: <strong>{selectedProvenance.metadata.file_name}</strong>
                    {selectedProvenance.metadata.total_chunks && ` (${selectedProvenance.metadata.total_chunks} chunks total)`}
                  </div>
                )}
              </div>
            </div>
          ) : selectedFinding !== null ? (
            (() => {
              const obsFindings = observations.map(obs => {
                const cache = obs.id ? observationDrillCache[obs.id] : null
                return {
                  type: findingTier(obs),
                  title: obs.title, detail: obs.detail,
                  source: obs.table_name ? `${obs.table_name}${obs.column ? ' · ' + obs.column : ''}` : obs.type,
                  sql: obs.sql, isObservation: true, observationId: obs.id,
                  data: cache?.rows || [],
                  rowCount: cache?.total || obs.affected_count || 0,
                  _drillLoading: cache?.loading, _drillError: cache?.error,
                }
              })
              const activeFinding = [...obsFindings, ...findings][selectedFinding]
              if (!activeFinding) return null
              const canShow = activeFinding.data?.length > 0 || activeFinding.isObservation || activeFinding.rowCount > 0
              if (!canShow) return null
              return (
                <DataViewer
                  finding={activeFinding}
                  onClose={() => setSelectedFinding(null)}
                />
              )
            })()
          ) : selectedDataSource && !selectedDataSource.loading && selectedDataSource.data ? (
            <DataViewer
              finding={{
                title: selectedDataSource.displayName || selectedDataSource.tableName,
                detail: `${selectedDataSource.totalRows?.toLocaleString() || '?'} total rows · ${selectedDataSource.columns?.length || '?'} columns`,
                type: 'data-source',
                data: selectedDataSource.data,
                sql: `SELECT * FROM "${selectedDataSource.tableName}" LIMIT ${selectedDataSource.limit}`,
                rowCount: selectedDataSource.totalRows,
              }}
              onClose={() => setSelectedDataSource(null)}
              totalRows={selectedDataSource.totalRows}
              currentLimit={selectedDataSource.limit}
              hasMore={selectedDataSource.hasMore}
              onChangeLimit={handleDataSourceLoadMore}
            />
          ) : selectedDataSource?.loading ? (
            <div className="data-viewer">
              <div className="dv-header">
                <div className="dv-header-left">
                  <h3 className="dv-title">{selectedDataSource.displayName || selectedDataSource.tableName}</h3>
                  <p className="dv-subtitle">Loading preview…</p>
                </div>
                <button className="dv-close" onClick={() => setSelectedDataSource(null)}>✕ Close</button>
              </div>
              <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'center', flex: 1, color: 'var(--gray-400)', fontSize: 13 }}>
                <div style={{ textAlign: 'center' }}>
                  <div className="dm-spinner" style={{ width: 28, height: 28, margin: '0 auto 8px' }} />
                  Loading data…
                </div>
              </div>
            </div>
          ) : selectedDataSource?.error ? (
            <div className="data-viewer">
              <div className="dv-header">
                <div className="dv-header-left">
                  <h3 className="dv-title">{selectedDataSource.displayName || selectedDataSource.tableName}</h3>
                  <p className="dv-subtitle" style={{ color: 'var(--red)' }}>⚠️ {selectedDataSource.error}</p>
                </div>
                <button className="dv-close" onClick={() => setSelectedDataSource(null)}>✕ Close</button>
              </div>
            </div>
          ) : (
          <div className="chat-scroll" ref={chatScrollRef}>

            {workState === 'empty' && (
              <div className="we-launchpad">
                <div className="we-track we-track-trust">
                  <div className="we-track-content">
                    <div className="we-track-label-wrap">
                      <div className="we-track-label">Start small and BUILD TRUST</div>
                      <div className="we-track-desc">Quick answers — verify what you already know</div>
                    </div>
                  </div>
                </div>

                <div className="we-track we-track-future">
                  <div className="we-track-content">
                    <div className="we-track-label-wrap">
                      <div className="we-track-label">Grow in confidence and BUILD THE FUTURE</div>
                      <div className="we-track-desc">Multi-step investigations that surface what humans miss</div>
                    </div>
                  </div>
                </div>
              </div>
            )}

            {messages.map((msg, i) => (
              <div key={i}>
                <Message role={msg.role} time={msg.time}>{msg.text}</Message>
                {msg.role === 'assistant' && msg.sql && (
                  <div style={{ display: 'flex', gap: 8, marginLeft: 16, marginBottom: 12 }}>
                    <button
                      className="we-pin-btn"
                      onClick={async () => {
                        const label = window.prompt('Metric name for profile:', msg.text?.replace(/\*\*/g, '').slice(0, 60))
                        if (!label) return
                        try {
                          await pinMetric(CUSTOMER_ID, {
                            label,
                            sql: msg.sql,
                            display_type: (msg.data?.length || 0) > 1 ? 'list' : 'number',
                            source_question: conversationHistory.filter(h => h.role === 'user').pop()?.content || '',
                          })
                          alert(`\u2713 "${label}" pinned to profile`)
                        } catch (e) {
                          alert('Failed to pin: ' + e.message)
                        }
                      }}
                    >📌 Pin to Profile</button>
                  </div>
                )}
              </div>
            ))}

            {/* Inline clarification card — shown for ad-hoc questions regardless of workState */}
            {clarification && workState !== 'clarification' && (
              <ClarificationCard
                stepLabel={clarification.stepLabel}
                question={clarification.question}
                options={clarification.options}
                defaultSelected={null}
                onSubmit={handleClarificationSubmit}
                {...clarificationCardLabels(clarification)}
              />
            )}

            {/* File-compare upload + mapping flow */}
            {compareMeta && (
              <CompareCard compareMeta={compareMeta} onComplete={handleCompareComplete} />
            )}

            {workState === 'complete' && completionStats.length > 0 && (
              <CompletionSummary title="Analysis Complete" stats={completionStats}
                text={<>Found <strong>{findings.length} items</strong>: {findings.filter(f=>f.type==='critical').length} critical, {findings.filter(f=>f.type==='warning').length} warnings, {findings.filter(f=>f.type==='compatible').length} compatible.</>}
              />
            )}

            {/* Continue / Re-export actions for completed efforts */}
            {(workState === 'complete' || workState === 'export') && findings.length > 0 && (
              <div className="we-complete-actions">
                <button className="we-continue-btn" onClick={() => {
                  setWorkState('empty')
                  setMessages(prev => [...prev, { role: 'assistant', text: `Continuing investigation for ${customer?.company_name || CUSTOMER_ID}. You can ask follow-up questions or start a new line of analysis. All previous findings are preserved in the right panel.`, time: 'Just now' }])
                }}>
                  Continue Investigation →
                </button>
                <button className="we-reexport-btn" onClick={() => setActiveTab('export')}>
                  Re-export
                </button>
              </div>
            )}

            {chatLoading && (
              <div className="typing-indicator">
                <div className="typing-dot" /><div className="typing-dot" /><div className="typing-dot" />
              </div>
            )}

          </div>
          )}
          <ChatInput onSend={handleSend} disabled={chatLoading} />
        </div>

        {/* ── RIGHT: PLAN → FINDINGS → ACTIONS → EXPORT ── */}
        <div className="context-panel">
          <div className="panel-tabs">
            {['plan', 'findings', 'actions', 'export'].map(tab => (
              <button key={tab} className={`panel-tab ${activeTab === tab ? 'active' : ''}`} onClick={() => setActiveTab(tab)}>
                {tab.charAt(0).toUpperCase() + tab.slice(1)}
                {tab === 'plan' && planSteps.length > 0 && workState === 'plan' && <span className="tab-badge green">{planSteps.length}</span>}
                {tab === 'plan' && workState === 'running' && <span className="tab-badge blue-badge">⟳</span>}
                {tab === 'findings' && observationsError && <span className="tab-badge amber" title={`Couldn't load the customer's findings: ${observationsError}`}>!</span>}
                {tab === 'findings' && (findings.length + summaryTotal(observationsSummary)) > 0 && (
                  <span
                    className={`tab-badge ${(observationsSummary?.critical || 0) > 0 || findings.some(f => f.type === 'critical') ? 'red' : 'green'}`}
                    title={`${summaryTotal(observationsSummary)} customer findings (FYI not counted) · ${findings.length} from this investigation`}
                  >{findings.length + summaryTotal(observationsSummary)}</span>
                )}
                {tab === 'actions' && actions.length > 0 && <span className="tab-badge amber">{actions.length}</span>}
              </button>
            ))}
          </div>
          <div className="panel-content">

            {/* PLAN TAB — investigation steps live here now */}
            {activeTab === 'plan' && (
              <div className="plan-tab-content">
                {workState === 'empty' && !chatLoading && (() => {
                  // Check if we have direct answer data from recent messages
                  const lastDataMsg = [...messages].reverse().find(m => m.role === 'assistant' && (m.data || m.sql))
                  if (lastDataMsg) {
                    return (
                      <div className="direct-answer-results">
                        {lastDataMsg.sql && (
                          <div className="dar-block">
                            <div className="dar-label">SQL Query</div>
                            <pre className="dar-sql">{lastDataMsg.sql}</pre>
                          </div>
                        )}
                        {lastDataMsg.data && lastDataMsg.data.length > 0 && (
                          <div className="dar-block">
                            <div className="dar-label">Results · {lastDataMsg.row_count || lastDataMsg.data.length} rows</div>
                            <div className="dar-table-wrap">
                              <table className="dar-table">
                                <thead>
                                  <tr>{(lastDataMsg.columns || Object.keys(lastDataMsg.data[0] || {})).map((col, i) => <th key={i}>{col}</th>)}</tr>
                                </thead>
                                <tbody>
                                  {lastDataMsg.data.slice(0, 50).map((row, i) => (
                                    <tr key={i}>
                                      {(lastDataMsg.columns || Object.keys(lastDataMsg.data[0] || {})).map((col, j) => (
                                        <td key={j}>{row[col] ?? '—'}</td>
                                      ))}
                                    </tr>
                                  ))}
                                </tbody>
                              </table>
                            </div>
                            {lastDataMsg.data.length > 50 && (
                              <div className="dar-truncated">Showing 50 of {lastDataMsg.data.length} rows</div>
                            )}
                          </div>
                        )}
                        {lastDataMsg.citations && lastDataMsg.citations.length > 0 && (
                          <div className="dar-block">
                            <div className="dar-label">Sources</div>
                            {lastDataMsg.citations.map((c, i) => (
                              <div key={i} className="dar-citation">{c.text}</div>
                            ))}
                          </div>
                        )}
                      </div>
                    )
                  }
                  return (
                    <div className="plan-empty">
                      <p>Ask a question in the chat to generate an investigation plan.</p>
                    </div>
                  )
                })()}

                {workState === 'empty' && chatLoading && (
                  <div className="plan-generating">
                    <div className="plan-generating-spinner" />
                    <p className="plan-generating-text">Analyzing your question…</p>
                    <p className="plan-generating-sub">Building investigation plan with available data sources</p>
                  </div>
                )}

                {workState === 'plan' && planSteps.length > 0 && (
                  <StepsCard
                    title="Investigation Plan"
                    subtitle={`${planSteps.length} steps`}
                    steps={planSteps}
                    showFooter
                    onRun={async () => {
                      // If any steps need method regeneration, do that first
                      const needsRegen = planSteps.some(s => s.needsRegen)
                      if (needsRegen) {
                        const updated = await regenerateMethods(planSteps)
                        setPlanSteps(updated)
                        // Don't auto-run — let user review the regenerated methods
                        return
                      }
                      handleRunInvestigation()
                    }}
                    onEditStep={handleEditStep}
                    onDeleteStep={handleDeleteStep}
                    onAddStep={handleAddStep}
                  />
                )}
                {workState === 'plan' && regenerating && (
                  <div className="regen-indicator">Regenerating step methods…</div>
                )}

                {workState === 'running' && runningSteps.length > 0 && (
                  <StepsCard
                    title="Investigation Running"
                    subtitle={`Step ${currentStepIndex + 1} of ${runningSteps.length}`}
                    steps={runningSteps}
                    showProgress={{ percent: runProgress, label: runProgress === 0 ? 'Starting…' : `${runProgress}%` }}
                  />
                )}
                {workState === 'running' && runningSteps.length === 0 && (
                  <div className="plan-generating">
                    <div className="plan-generating-spinner" />
                    <p className="plan-generating-text">Launching investigation…</p>
                    <p className="plan-generating-sub">Approving plan and preparing engines</p>
                  </div>
                )}

                {workState === 'clarification' && (
                  <>
                    {runningSteps.length > 0 && (
                      <StepsCard title="Investigation Paused" subtitle={<span className="paused-badge"><span className="paused-dot" /> Waiting for input</span>} steps={runningSteps} />
                    )}
                    {clarification && (
                      <ClarificationCard stepLabel={clarification.stepLabel} question={clarification.question} options={clarification.options} defaultSelected={null} onSubmit={handleClarificationSubmit} {...clarificationCardLabels(clarification)} />
                    )}
                  </>
                )}

                {workState === 'error' && (
                  <>
                    <StepsCard title="Investigation Paused" subtitle={<span style={{ fontSize: 12, fontWeight: 600, color: 'var(--red)' }}>Step failed</span>} steps={runningSteps} showProgress={{ percent: runProgress, label: `Stopped at ${runProgress}%`, error: true }} />
                    {stepError && <ErrorCard title={stepError.title} text={stepError.text} code={stepError.code} onRetry={handleRetryStep} onSkip={handleSkipStep} onHelp={() => console.log('Upload missing data')} />}
                  </>
                )}

                {(workState === 'complete' || workState === 'export') && runningSteps.length > 0 && (
                  <StepsCard
                    title="Investigation Complete"
                    subtitle={`${runningSteps.length} of ${runningSteps.length} steps`}
                    steps={runningSteps.map(s => ({ ...s, status: 'complete' }))}
                  />
                )}

                {/* Data sources — collapsible at bottom of plan tab */}
                {hasDataSources(dataSources) && (
                  <details className="plan-data-sources" open={Object.keys(dataSources.errors || {}).length > 0 || undefined}>
                    <summary className="plan-data-summary">
                      Data Sources ({dataSources.errors?.tables ? "tables couldn't load" : `${dataSources.tables.length} tables`} · {dataSources.errors?.connections ? "connections couldn't load" : `${dataSources.connections.length} connections`})
                    </summary>
                    {Object.keys(dataSources.errors || {}).map(k => (
                      <LoadError key={k} inline what={k} error={dataSources.errors[k]} onRetry={loadDataSources} />
                    ))}
                    <DataTab tables={dataSources.tables} uploads={dataSources.uploads} connections={dataSources.connections} customerId={CUSTOMER_ID} onTableClick={handleDataSourceClick} selectedTable={selectedDataSource?.tableName} onDataChanged={loadDataSources}
                      loadFailed={Object.keys(dataSources.errors || {}).length > 0} />
                  </details>
                )}
              </div>
            )}

            {activeTab === 'findings' && (() => {
              // Merge observations (auto-findings) with investigation findings
              // the platform's tiering (utils/findingTier): error is Critical, info is Info,
              // an informational check's finding is FYI
              const observationFindings = observations.map(obs => ({
                type: findingTier(obs),
                couldNotCheck: couldNotCheck(obs),
                lastCheckReason: obs.last_check_reason,
                lastCheckedAt: obs.last_checked_at,
                title: obs.title,
                detail: obs.detail,
                source: obs.table_name ? `${obs.table_name}${obs.column ? ' · ' + obs.column : ''}` : obs.type,
                sql: obs.sql,
                data: [],  // populated lazily on click via observationDrillCache
                rowCount: obs.affected_count || (obs.data && obs.data.count) || 0,
                isObservation: true,
                observationId: obs.id,
              }))
              // Inject cached drill rows into observation findings
              const mergedWithData = observationFindings.map(f => {
                const cache = f.observationId ? observationDrillCache[f.observationId] : null
                if (!cache) return f
                return { ...f, data: cache.rows || [], _drillLoading: cache.loading, _drillError: cache.error }
              })
              const merged = [...mergedWithData, ...findings]
              return <FindingsTab findings={merged} customerSummary={observationsSummary} customerError={observationsError} customerLoading={observationsLoading} selectedIndex={selectedFinding} onSelect={async (index) => {
                  setSelectedFinding(index)
                  if (index === null) return
                  // Find the observation finding at this index
                  const allFindings = [...observationFindings, ...findings]
                  const f = allFindings[index]
                  if (!f?.isObservation || !f?.observationId) return
                  const obsId = f.observationId
                  if (observationDrillCache[obsId]) return // already fetched
                  // Mark loading
                  setObservationDrillCache(prev => ({ ...prev, [obsId]: { loading: true, rows: [], columns: [] } }))
                  try {
                    const API = API_BASE_CONFIG
                    const tok = localStorage.getItem('xlr8_token') || ''
                    const res = await fetch(`${API}/api/observations/${CUSTOMER_ID}/drilldown/${obsId}`, {
                      headers: { 'Authorization': `Bearer ${tok}` }
                    })
                    if (res.ok) {
                      const drill = await res.json()
                      setObservationDrillCache(prev => ({
                        ...prev,
                        [obsId]: { loading: false, rows: drill.rows || [], columns: drill.columns || [], total: drill.total_rows || 0 }
                      }))
                    } else {
                      setObservationDrillCache(prev => ({ ...prev, [obsId]: { loading: false, rows: [], columns: [], error: `HTTP ${res.status}` } }))
                    }
                  } catch(e) {
                    setObservationDrillCache(prev => ({ ...prev, [obsId]: { loading: false, rows: [], columns: [], error: e.message } }))
                  }
                }} onProvenanceClick={handleProvenanceClick} onPin={async (finding) => {
                // Handle dismiss for observations
                // A dismissal needs a reason (findings status v2); cancelling or leaving it
                // blank dismisses nothing.
                if (finding.isObservation && finding.observationId) {
                  const reason = (window.prompt('Why is this finding being dismissed?', '') || '').trim()
                  if (!reason) return
                  try {
                    await dismissObservation(finding.observationId, reason)
                    const gone = observations.find(o => o.id === finding.observationId)
                    setObservations(prev => prev.filter(o => o.id !== finding.observationId))
                    setObservationsSummary(prev => summaryWithout(prev, gone))
                  } catch (e) {
                    console.error('Failed to dismiss observation:', e)
                    window.alert(`The finding was not dismissed: ${e.message || e}`)
                  }
                  return
                }
                // Normal pin behavior for investigation findings
                const label = window.prompt('Metric name for profile:', finding.title)
                if (!label) return
                const displayType = finding.data?.length > 1 ? 'list' : 'number'
                try {
                  await pinMetric(CUSTOMER_ID, {
                    label,
                    sql: finding.sql,
                    display_type: displayType,
                    source_question: finding.detail || finding.title,
                  })
                  alert(`✓ "${label}" pinned to profile`)
                } catch (e) {
                  alert('Failed to pin: ' + e.message)
                }
              }} />
            })()}
            {activeTab === 'actions' && <ActionsTab actions={actions} onUpdate={setActions} />}
            {activeTab === 'export' && <ExportTab showReady={workState === 'export'} exportResult={exportResult} customerId={CUSTOMER_ID} customer={customer} findings={findings} actions={actions} planTitle={planTitle} workState={workState} onGenerate={handleExport} lastDataMsg={[...messages].reverse().find(m => m.role === 'assistant' && m.data && m.data.length > 0) || null} />}
          </div>
        </div>
      </div>
    </div>
  )
}
